<template>
  <div>
    <span v-if="isLoading" class="loading-wrapper">
      <v-progress-circular color="realEstate" class="loading" size="20" width="2" indeterminate />
    </span>
    <div ref="mapDrawing" id="mapDrawing"></div>
  </div>
</template>

<script>
import { latLong } from '../../util/enums'
import moment from 'moment-timezone'

import iconSvg from '../../util/iconsSVG/Iconsvg'
import { getCustomerAppointments, getCustomerAppointmentId } from '../../services/dashboard/customerDashboardService'
import { getAppointments, getAppointmentId } from '../../services/dashboard/dashboardService'
import { mapPinContent, mapPinContentLoading } from '../../components/dashboard/mapContent'
import { formatDateForFilters, formatDateWithTimezone } from '@/util/catalog'
import { mapActions } from 'vuex'
import { mapLoader } from '@/util/mapLoader'

export default {
  name: 'DrawingMap',

  computed: {
    apiKey() {
      return process.env.VUE_APP_GOOGLE_MAPS_API_KEY
    },

    mapWrapper() {
      return this.$refs.mapDrawing
    },

    oldToken() {
      return localStorage.getItem('old_token')
    },

    isAdminOrPartner() {
      const roles = localStorage.getItem('access_roles')
      const userRoles = roles?.split(',')
      return userRoles?.includes('ROLE_ADMIN') || userRoles?.includes('ROLE_PARTNER')
    },
  },

  methods: {
    ...mapActions(['showErrorMessage']),

    setMarketIcon(item, group = false) {
      if (group) return { url: `${iconSvg.iconsvg('GROUP')}` }

      return {
        url: `${iconSvg.iconsvg(
          item.type,
          item.appointment_deadline_time,
          item.reschedule_count,
          item.is_urgent
        )}`
      }
    },

    createMarker(item) {
      if (this.filters.category === 'GROUP') {
        return new google.maps.Marker({
          map: this.googleMap,
          position: { lat: item.latitude, lng: item.longitude },
          title: 'Group',
          code: item.code,
          type: item.type,
          icon: this.setMarketIcon(item, true),
          status: item.status,
          date: item.date,
          id: item.id,
          latitude: item.latitude,
          longitude: item.longitude,
          appointments: item.appointments,
          partner: item.partner,
          scheduled_by: item.scheduled_by,
          hostValue: item.host_value,
          totalValue: item.total_value,
          host: item.host,
          additional_data: item.additional_data,
          visitor_mail: item.visitor_mail,
          confirmation: item.confirmation,
        })
      }

      return new google.maps.Marker({
        map: this.googleMap,
        position: { lat: item.latitude_meeting, lng: item.longitude_meeting },
        title: 'Appointment',
        code: item.code,
        type: item.type,
        icon: this.setMarketIcon(item, false),
        status: item.status,
        date: item.date,
        id: item.id,
        group_id: item.group_id,
        group_code: item.group_code,
        latitude: item.latitude_meeting,
        longitude: item.longitude_meeting,
        additional_data: item.additional_data,
        visitor_mail: item.visitor_mail,
        confirmation: item.confirmation,
        host: item.host
      })
    },

    createMarkerForGroupedAppointments(item) {
      return new google.maps.Marker({
        map: this.googleMap,
        position: { lat: item.latitude_meeting, lng: item.longitude_meeting },
        title: 'Appointment',
        code: item.code,
        type: item.type,
        icon: this.setMarketIcon(item, false),
        status: item.status,
        date: item.date,
        id: item.id,
        group_id: item.group_id,
        group_code: item.group_code,
        latitude: item.latitude_meeting,
        longitude: item.longitude_meeting,
        additional_data: item.additional_data,
        visitor_mail: item.visitor_mail
      })
    },

    createGroupedMarker(item) {
      return new google.maps.Marker({
        map: this.googleMap,
        position: { lat: item.latitude_meeting, lng: item.longitude_meeting },
        title: 'Grouped Appointment',
        code: item.code,
        type: item.type,
        icon: this.setMarketIcon(item, false),
        status: item.status,
        date: item.date,
        id: item.id,
        group_id: item.group_id,
        latitude: item.latitude_meeting,
        longitude: item.longitude_meeting,
        additional_data: item.additional_data,
        visitor_mail: item.visitor_mail
      })
    },

    addEventListenerToMarkers() {
      this.showHideMapActionMenu(true)

      this.markers.forEach((marker) => {
        marker.addListener('dblclick', () => {
          this.showMarkerInfo(marker.id)
        })
      })
    },

    addEventListenerToGroupMarkers() {
      this.showHideMapActionMenu()

      this.markers.forEach((marker) => {
        marker.addListener('dblclick', () => {
          this.showMarkerInfoFromGroup(marker)
        })
      })
    },

    resetMapSelectionAndMarkers() {
      this.$emit('group', null)
      this.selectedMarkers = []
      if (this.drawnPolygon) this.drawnPolygon.setMap(null)
      this.markers?.forEach((marker) => marker.setMap(null))
      this.$emit('selectedMarkers', this.selectedMarkers)
      this.markers = []
    },

    resetSelection() {
      this.$emit('group', null)
      this.selectedMarkers = []
      if (this.drawnPolygon) this.drawnPolygon.setMap(null)
    },

    initCustomPolygonDrawer() {
      const control = document.createElement('div')
      control.className = 'custom-draw-control'

      const button = document.createElement('button')
      button.type = 'button'
      button.className = 'custom-draw-button'
      button.title = this.$t('mapDrawing.drawPolygon')
      button.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#21566E" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polygon points="12 2 22 8.5 18 20 6 20 2 8.5 12 2"></polygon>
        </svg>
      `
      button.addEventListener('click', () => {
        if (this.isDrawing) this.cancelDrawing()
        else this.startDrawing()
      })

      const hint = document.createElement('div')
      hint.className = 'custom-draw-hint'

      control.appendChild(button)
      control.appendChild(hint)

      this.drawControl = control
      this.drawButton = button
      this.drawHint = hint
      this.googleMap.controls[this.google.maps.ControlPosition.TOP_CENTER].push(control)

      document.addEventListener('keydown', this.handleEscKey)
    },

    startDrawing() {
      if (this.drawnPolygon) {
        this.drawnPolygon.setMap(null)
        this.drawnPolygon = null
      }

      this.isDrawing = true
      this.drawingPath = []
      this.drawButton?.classList.add('is-active')
      this.googleMap.setOptions({ draggableCursor: 'crosshair' })

      this.tempPolyline = new this.google.maps.Polyline({
        map: this.googleMap,
        path: [],
        strokeColor: this.polygonStyle().strokeColor,
        strokeWeight: this.polygonStyle().strokeWeight,
        clickable: false,
      })

      this.clickListener = this.google.maps.event.addListener(this.googleMap, 'click', (event) => {
        this.drawingPath.push(event.latLng)
        this.tempPolyline.setPath(this.drawingPath)
        if (!this.startVertexMarker) this.createStartVertexMarker(event.latLng)
      })

      // rubber-band: trailing segment from last placed vertex to the cursor
      this.mousemoveListener = this.google.maps.event.addListener(this.googleMap, 'mousemove', (event) => {
        if (!this.drawingPath.length) return
        this.tempPolyline.setPath([...this.drawingPath, event.latLng])
      })

      this.updateDrawHint()
    },

    createStartVertexMarker(latLng) {
      this.startVertexMarker = new this.google.maps.Marker({
        map: this.googleMap,
        position: latLng,
        zIndex: 1000,
        icon: {
          path: this.google.maps.SymbolPath.CIRCLE,
          scale: 6,
          fillColor: '#fff',
          fillOpacity: 1,
          strokeColor: this.polygonStyle().strokeColor,
          strokeWeight: 2,
        },
      })

      this.startVertexListener = this.google.maps.event.addListener(this.startVertexMarker, 'click', () => {
        if (this.drawingPath.length >= 3) this.finishDrawing()
      })
    },

    finishDrawing() {
      const polygon = new this.google.maps.Polygon({
        map: this.googleMap,
        paths: this.drawingPath,
        ...this.polygonStyle(),
      })

      this.google.maps.event.addListener(polygon.getPath(), 'set_at', () => {
        this.updateMarkerClusters(polygon)
      })
      this.google.maps.event.addListener(polygon.getPath(), 'insert_at', () => {
        this.updateMarkerClusters(polygon)
      })
      this.google.maps.event.addListener(polygon.getPath(), 'remove_at', () => {
        this.updateMarkerClusters(polygon)
      })

      this.drawnPolygon = polygon
      this.teardownDrawing()
      this.updateMarkerClusters(polygon)
      this.updateDrawHint()
    },

    cancelDrawing() {
      this.teardownDrawing()
      this.updateDrawHint()
    },

    clearPolygon() {
      if (this.drawnPolygon) {
        this.drawnPolygon.setMap(null)
        this.drawnPolygon = null
      }
      this.selectedMarkers = []
      this.$emit('selectedMarkers', [])
      this.updateDrawHint()
    },

    handleEscKey(event) {
      if (event.key !== 'Escape') return
      if (this.isDrawing) this.cancelDrawing()
      else if (this.drawnPolygon) this.clearPolygon()
    },

    updateDrawHint() {
      if (!this.drawHint) return

      if (this.isDrawing) {
        this.drawHint.textContent = this.$t('mapDrawing.drawingHint')
        this.drawHint.classList.add('is-visible')
      } else if (this.drawnPolygon) {
        this.drawHint.textContent = this.$t('mapDrawing.clearHint')
        this.drawHint.classList.add('is-visible')
      } else {
        this.drawHint.classList.remove('is-visible')
      }
    },

    teardownDrawing() {
      if (this.tempPolyline) {
        this.tempPolyline.setMap(null)
        this.tempPolyline = null
      }
      if (this.startVertexListener) {
        this.google.maps.event.removeListener(this.startVertexListener)
        this.startVertexListener = null
      }
      if (this.startVertexMarker) {
        this.startVertexMarker.setMap(null)
        this.startVertexMarker = null
      }
      if (this.clickListener) {
        this.google.maps.event.removeListener(this.clickListener)
        this.clickListener = null
      }
      if (this.mousemoveListener) {
        this.google.maps.event.removeListener(this.mousemoveListener)
        this.mousemoveListener = null
      }
      this.drawingPath = []
      this.isDrawing = false
      this.drawButton?.classList.remove('is-active')
      this.googleMap.setOptions({ draggableCursor: null })
    },

    updateMarkerClusters(polygon) {
      const selectedMarkers = this.markers.filter(marker => {
        return this.google.maps.geometry.poly.containsLocation(marker.getPosition(), polygon)
      })

      const markersData = selectedMarkers.map(marker => {
        return {
          id: marker.id,
          code: marker.code,
          type: marker.type,
          status: marker.status,
          date: marker.date,
          latitude: marker.latitude,
          longitude: marker.longitude,
          group_id: marker.group_id,
          group_code: marker.group_code,
          additional_data: marker.additional_data,
          visitor_mail: marker.visitor_mail,
          confirmation: marker.confirmation,
          host: marker.host,
          appointments: marker.appointments,
          partner: marker.partner,
          scheduled_by: marker.scheduled_by,
          hostValue: marker.hostValue,
          totalValue: marker.totalValue,
        }
      })

      this.selectedMarkers = selectedMarkers
      this.$emit('selectedMarkers', markersData)
    },

    // add initial category (APPOINTMENT) if none is selected (default
    setInitialCategoryToFetch(filters) {
      const category = filters?.category

      if (!category) return 'APPOINTMENT'
      return category
    },

    // add initial date filter (- 15 days from today) if none is selected
    setInitialDateFilterSelection(filters) {
      const startDate = filters?.startDate

      if (!startDate) {
        return formatDateForFilters()
      }

      return startDate
    },

    // add all status filter if none is selected
    setInitialStatusFilterSelection(filters, isInitialLoad) {
      const status = filters?.status
      const category = filters?.category
      const defaultStatus = ['DRAFT', 'REJECTED']

      const isEmpty = !status || status.length === 0

      if (category === 'GROUP') return isEmpty ? [] : status

      if (isEmpty) return isInitialLoad ? defaultStatus : []

      return status
    },

    async fetchAppointmentsAndSetMarkers(filters, showGroupedAppointments = false) {
      this.resetMapSelectionAndMarkers()
      try {
        this.isLoading = true
        this.filters = { ...this.filters, ...filters }

        const isInitialLoad = !this.hasAppliedDefaultStatus

        this.filters.status = this.setInitialStatusFilterSelection(this.filters, isInitialLoad)
        this.filters.startDate = this.setInitialDateFilterSelection(this.filters)
        this.filters.category = this.setInitialCategoryToFetch(this.filters)

        if (isInitialLoad) this.hasAppliedDefaultStatus = true

        let resultData

        if (this.isAdminOrPartner) {
          const { result } = await getAppointments(
            this.oldToken,
            this.filters.status,
            this.filters.serviceType,
            undefined,
            this.filters.hostsIds,
            undefined,
            this.filters.startDate && formatDateWithTimezone(this.filters.startDate),
            this.filters.endDate && formatDateWithTimezone(this.filters.endDate),
            this.filters.startTime,
            this.filters.endTime,
            undefined,
            undefined,
            undefined,
            this.filters?.category,
            this.filters?.search,
            this.filters?.realEstate,
            this.filters?.branches,
            this.filters?.confirmed,
            this.filters?.confirmationEmailSent,
          )

          resultData = result
        } else {
          const { result } = await getCustomerAppointments(
            this.filters.status,
            this.filters.serviceType,
            undefined,
            this.filters.hostsIds,
            undefined,
            this.filters.startDate && formatDateWithTimezone(this.filters.startDate),
            this.filters.endDate && formatDateWithTimezone(this.filters.endDate),
            this.filters.startTime,
            this.filters.endTime,
            undefined,
            undefined,
            this.filters?.category,
            this.filters?.search,
            this.filters?.realEstate,
            this.filters?.branches,
            this.filters?.confirmed,
            this.filters?.confirmationEmailSent,
          )

          resultData = result
        }

        let filterGroupedMarkers = []

        if (showGroupedAppointments) filterGroupedMarkers = resultData
        else filterGroupedMarkers = resultData.filter((marker) => !marker.group_id)

        filterGroupedMarkers.forEach((item) => {
          const marker = this.createMarker(item)
          this.markers.push(marker)
        })
      } catch (error) {
        this.showErrorMessage(`Error while fetch appointments ${error}`)
      } finally {
        if (this.filters.category === 'GROUP') this.addEventListenerToGroupMarkers()
        else this.addEventListenerToMarkers()

        this.isLoading = false
      }
    },

    async showMarkerInfo(id) {
      this.markerInfo?.close()
      const marker = this.markers.find(marker => marker.id === id)
      this.showMarkerInfoLoading(marker)

      try {
        let response

        if (this.isAdminOrPartner) {
          response = await getAppointmentId(this.oldToken, id)
        } else {
          response = await getCustomerAppointmentId(id)
        }

        this.markerInfo?.close()

        this.markerInfo = new this.google.maps.InfoWindow({
          content: mapPinContent(response),
          maxWidth: 520,
          minWidth: 520,
        })

        this.markerInfo.open({
          map: this.googleMap,
          anchor: marker
        })
      } catch ({ data }) {
        this.showErrorMessage(`Error while fetch appointment: ${data?.message}`)
        this.markerInfo?.close()
      }
    },

    showMarkersFromGroup(group) {
      this.markers?.forEach((marker) => marker.setMap(null))

      this.$nextTick(() => {
        group.appointments.forEach((appointment) => {
          const appointmentMarker = { ...appointment, latitude: appointment.latitude_meeting, longitude: appointment.longitude_meeting }
          const marker = this.createMarkerForGroupedAppointments(appointmentMarker)
          this.markers.push(marker)
        })
      })

      this.$nextTick(() => {
        this.$emit('showCloseTooltip')
        this.addEventListenerToMarkers()
        this.hideMapActionMenu()
      })
    },

    showMarkerInfoFromGroup(marker) {
      const groupData = {
        id: marker.id,
        date: marker.date,
        status: marker.status,
        code: marker.code,
        totalAppointments: marker.appointments?.length,
        scheduledBy: marker.scheduled_by,
        partner: marker.partner,
        hostValue: marker.hostValue,
        totalValue: marker.totalValue,
        host: marker.host,
        additionalData: marker.additional_data,
      }

      this.$emit('group', groupData)

      const appointmentsWithDate = marker.appointments.map(appointment => ({
        ...appointment,
        date: appointment.date || marker.date
      }))

      this.$emit('selectedMarkers', appointmentsWithDate)
      this.$nextTick(() => this.showMarkersFromGroup(marker))
    },

    showMarkerInfoLoading(marker) {
      this.markerInfo = new this.google.maps.InfoWindow({
        content: mapPinContentLoading(),
      })

      this.markerInfo.open({
        map: this.googleMap,
        anchor: marker
      })
    },

    initMap() {
      if (!this.apiKey) {
        console.error('Google Maps api key not found')
        return
      }

      if (!this.mapWrapper) {
        console.error('Map wrapper not found')
        return
      }

      mapLoader
        .load()
        .then((google) => {
          this.google = google
          this.googleMap = new this.google.maps.Map(this.mapWrapper, this.googleMapOptions(google))
          this.initCustomPolygonDrawer()
        })
        .then(() => {
          this.fetchAppointmentsAndSetMarkers()
        })
    },

    googleMapOptions() {
      const country = localStorage.getItem('user_country') ?? 'BR'

      return {
        zoom: 5,
        center: latLong[country],
        mapTypeId: this.google.maps.MapTypeId.ROADMAP,
        streetViewControl: false,
        fullscreenControl: false,
        mapTypeControl: false,
      }
    },

    polygonStyle() {
      return {
        fillColor: "#F37A76",
        fillOpacity: 0.3,
        strokeColor: "#21566E",
        strokeWeight: 3,
        clickable: true,
        editable: true,
      }
    },

    showHideMapActionMenu(visibility) {
      if (!this.drawControl) return
      this.drawControl.style.display = visibility ? 'flex' : 'none'
      if (!visibility && this.isDrawing) this.cancelDrawing()
    },

    hideMapActionMenu() {
      if (!this.drawControl) return
      this.drawControl.style.display = 'none'
      if (this.isDrawing) this.cancelDrawing()
    },
  },

  data() {
    return {
      filters: null,
      isLoading: false,
      google: null,
      googleMap: null,
      drawnPolygon: null,
      drawControl: null,
      drawButton: null,
      drawHint: null,
      isDrawing: false,
      drawingPath: [],
      tempPolyline: null,
      clickListener: null,
      mousemoveListener: null,
      startVertexMarker: null,
      startVertexListener: null,
      selectedMarkers: [],
      markerClusters: null,
      markerInfo: null,
      markers: [],
      category: 'APPOINTMENT',
      hasAppliedDefaultStatus: false,

      group: null,

      timezone: moment.tz.guess(),
    }
  },

  mounted() {
    this.initMap()
  },

  beforeDestroy() {
    document.removeEventListener('keydown', this.handleEscKey)
  },
}
</script>

<style>
.custom-draw-control {
  display: flex;
  align-items: center;
  gap: 6px;
}

.custom-draw-hint {
  display: none;
  margin: 10px 10px 10px 0;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  color: #21566E;
  white-space: nowrap;
  background-color: #fff;
  border-radius: 16px;
  box-shadow: 0 1px 4px -1px rgba(0, 0, 0, 0.3);
}

.custom-draw-hint.is-visible {
  display: block;
}

.custom-draw-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  margin: 10px;
  background-color: #fff;
  border: 0;
  border-radius: 2px;
  box-shadow: 0 1px 4px -1px rgba(0, 0, 0, 0.3);
  cursor: pointer;
}

.custom-draw-button:hover {
  background-color: #f5f5f5;
}

.custom-draw-button.is-active {
  background-color: #21566E;
}

.custom-draw-button.is-active svg {
  stroke: #fff;
}
</style>

<style scoped>
#mapDrawing {
  width: 100%;
  height: calc(100vh - 0px);
  overflow-y: hidden;
}

.loading-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  width: 50px;
  height: 50px;
  top: 80px;
  left: 50%;
  z-index: 2;
  border-radius: 50%;
  backdrop-filter: blur(10px);
  transform: translate(-50%, -50%);
  background-color: rgba(255, 255, 255, 0.65);
  box-shadow: -2px 6px 12px 0 rgba(0, 0, 0, 0.2) !important;
}
</style>
